import React, { useEffect, useState, useCallback } from 'react';
import { Modal, List, Input, Button, message, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { getJobActivityFeed, createJobActivityFeed } from '../../api/jobs';
import { JobActivity } from '../../types';
import { format } from 'date-fns';
import styled from 'styled-components';

const { TextArea } = Input;

const StyledModal = styled(Modal)`
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
`;

const MessageItem = styled(List.Item)`
  flex-direction: column;
  align-items: flex-start !important; // Override default Antd alignment
`;

const MessageMeta = styled.div`
  color: #888;
  font-size: 0.8em;
  margin-bottom: 4px;
`;

interface BroadcastMessageModalProps {
    jobId: string | null;
    isVisible: boolean;
    onClose: () => void;
}

const BroadcastMessageModal: React.FC<BroadcastMessageModalProps> = ({
    jobId,
    isVisible,
    onClose,
}) => {
    const { t } = useTranslation();
    const [messages, setMessages] = useState<JobActivity[]>([]);
    const [newMessage, setNewMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const fetchMessages = useCallback(async () => {
        if (!jobId) return;
        setLoading(true);
        try {
            const response = await getJobActivityFeed(jobId);
            // Assuming response.data is an array of JobActivity
            // Sort by created_at descending (newest first)
            setMessages(response.data.sort((a: JobActivity, b: JobActivity) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            ));
        } catch (error) {
            message.error(t('ERROR_FETCHING_MESSAGES'));
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    }, [jobId, t]);

    useEffect(() => {
        if (isVisible && jobId) {
            fetchMessages();
        } else {
            // Clear messages when modal is hidden or jobId is null
            setMessages([]);
            setNewMessage('');
        }
    }, [isVisible, jobId, fetchMessages]);

    const handleSend = async () => {
        if (!jobId || !newMessage.trim()) return;
        setSending(true);
        try {
            await createJobActivityFeed(jobId, newMessage);
            message.success(t('MESSAGE_SENT_SUCCESS'));
            setNewMessage(''); // Clear input
            fetchMessages(); // Refresh messages list
        } catch (error) {
            message.error(t('ERROR_SENDING_MESSAGE'));
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    return (
        <StyledModal
            title={t('BROADCAST_MESSAGE_MODAL_TITLE')}
            visible={isVisible}
            onCancel={onClose}
            footer={[
                <Button key="back" onClick={onClose}>
                    {t('CANCEL')}
                </Button>,
                <Button key="submit" type="primary" loading={sending} onClick={handleSend}>
                    {t('SEND')}
                </Button>,
            ]}
            width={600}
        >
            <Spin spinning={loading}>
                <List
                    header={<div>{t('LAST_MESSAGES_HEADER')}</div>}
                    bordered
                    dataSource={messages}
                    renderItem={(item) => (
                        <MessageItem key={item.id}>
                            <MessageMeta>
                                {format(new Date(item.created_at), 'PPpp')} {/* Format date */}
                            </MessageMeta>
                            <div>{item.text}</div>
                        </MessageItem>
                    )}
                    style={{ marginBottom: '20px', maxHeight: '300px', overflowY: 'auto' }}
                    locale={{ emptyText: t('NO_MESSAGES_YET') }}
                />
            </Spin>
            <TextArea
                rows={4}
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={t('BROADCAST_MESSAGE_PLACEHOLDER')}
                disabled={sending}
            />
        </StyledModal>
    );
};

export default BroadcastMessageModal; 